/**
 * MagicLinkLanding — `/auth/magic-link?token=...`
 *
 * The user clicks the link in their email and lands here. We POST the
 * token to `/api/auth/magic-link/verify`, hydrate the session via
 * AuthContext, and redirect to /dashboard (or the original `next` path
 * if the email-request included one).
 *
 * Visual style follows the Login page — node-network background,
 * Palantir bracket headers, gold + violet accent. Mirrors the
 * /pricing/success and /billing/success aesthetics.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, AlertTriangle, Loader2, ArrowRight, Mail } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

// Module-level dedupe set — survives React StrictMode's mount/unmount/mount
// dance in dev, which would otherwise consume the one-time token twice
// (first redemption succeeds → second gets 410 → user sees the error
// state). A page reload starts a fresh module so legitimate retries
// still work.
const _verifiedTokens = new Set();

export default function MagicLinkLanding() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { applySession } = useAuth();
  const [state, setState] = useState('verifying'); // 'verifying' | 'success' | 'error'
  const [errorMsg, setErrorMsg] = useState('');
  const [user, setUser] = useState(null);

  const token = params.get('token');
  // StrictMode in dev mounts effects twice — that would consume the
  // one-time token on the first mount and 410 on the second. The
  // module-level `_verifiedTokens` set guards against that, plus this
  // local ref guards against in-render double-fires.
  const verifyStartedRef = useRef(false);

  /* eslint-disable -- v6 react-hooks/set-state-in-effect false positive on the verify side-effect. */
  useEffect(() => {
    if (!token) {
      setState('error');
      setErrorMsg('Missing token in URL. Please request a new sign-in link.');
      return;
    }
    if (verifyStartedRef.current || _verifiedTokens.has(token)) return;
    verifyStartedRef.current = true;
    _verifiedTokens.add(token);
    api
      .post('/auth/magic-link/verify', { token })
      .then(({ data }) => {
        // Hydrate the AuthContext + cookie so subsequent /api calls work.
        // The HttpOnly session_token cookie is set by the backend.
        if (applySession) {
          applySession(data.token, data.user);
        }
        setUser(data.user);
        setState('success');
        toast.success(data.is_new ? `Welcome to the platform.` : `Welcome back.`);
        // Brief pause so the user sees the success state, then redirect.
        const next = data.is_new ? '/dashboard?welcome=1' : '/dashboard';
        setTimeout(() => nav(next), 900);
      })
      .catch((err) => {
        setState('error');
        setErrorMsg(err?.response?.data?.detail || 'Sign-in link is invalid.');
      });
  }, [token, applySession, nav]);
  /* eslint-enable */

  return (
    <main
      data-testid="magic-link-landing"
      className="relative min-h-screen overflow-hidden text-white flex items-center justify-center"
      style={{
        background:
          'radial-gradient(1200px 600px at 80% -10%, rgba(174,82,234,0.12), transparent 60%),' +
          'radial-gradient(900px 600px at -10% 110%, rgba(174,82,234,0.10), transparent 60%),' +
          'linear-gradient(180deg, #050505 0%, #0A0A0A 50%, #050505 100%)',
      }}
    >
      <div className="relative rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-8 lg:p-10 shadow-[0_30px_120px_-40px_rgba(174,82,234,0.4)] max-w-md w-full mx-4">
        {state === 'verifying' && (
          <div data-testid="magic-link-verifying">
            <div
              className="h-12 w-12 rounded-md flex items-center justify-center border"
              style={{
                backgroundColor: 'rgba(174,82,234,0.10)',
                color: '#AE52EA',
                borderColor: 'rgba(174,82,234,0.40)',
              }}
            >
              <Loader2 size={22} className="animate-spin" />
            </div>
            <div className="mt-4 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
              [VERIFYING · SIGN-IN LINK]
            </div>
            <h1 className="display text-2xl mt-1.5">One moment…</h1>
            <p className="text-sm text-white/55 mt-3">
              Confirming your sign-in link and provisioning your session.
            </p>
          </div>
        )}

        {state === 'success' && (
          <div data-testid="magic-link-success">
            <div
              className="h-12 w-12 rounded-md flex items-center justify-center border"
              style={{
                backgroundColor: 'rgba(16,185,129,0.10)',
                color: '#10B981',
                borderColor: 'rgba(16,185,129,0.40)',
              }}
            >
              <CheckCircle2 size={22} />
            </div>
            <div className="mt-4 text-[10px] uppercase tracking-[0.22em] text-emerald-400 font-bold">
              [SESSION · ACTIVE]
            </div>
            <h1 className="display text-2xl mt-1.5">
              {user ? `Welcome, ${user.name || user.email}.` : 'Welcome.'}
            </h1>
            <p className="text-sm text-white/55 mt-3">
              Taking you to your dashboard…
            </p>
          </div>
        )}

        {state === 'error' && (
          <div data-testid="magic-link-error">
            <div
              className="h-12 w-12 rounded-md flex items-center justify-center border"
              style={{
                backgroundColor: 'rgba(250,176,5,0.10)',
                color: '#F59E0B',
                borderColor: 'rgba(250,176,5,0.40)',
              }}
            >
              <AlertTriangle size={22} />
            </div>
            <div className="mt-4 text-[10px] uppercase tracking-[0.22em] text-amber-400 font-bold">
              [LINK · INVALID]
            </div>
            <h1 className="display text-2xl mt-1.5">Can&apos;t sign you in.</h1>
            <p className="text-sm text-white/65 mt-3">{errorMsg}</p>
            <div className="mt-6 flex flex-wrap gap-2">
              <Link
                to="/login"
                data-testid="magic-link-request-new"
                className="inline-flex items-center gap-2 rounded-md bg-[#8B3FC5] text-white text-[11px] font-bold px-4 py-2 uppercase tracking-[0.22em] hover:bg-[#AE52EA] transition-colors"
              >
                <Mail size={12} />
                Request a new link
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 rounded-md border border-white/15 text-white/80 text-[11px] font-bold px-4 py-2 uppercase tracking-[0.22em] hover:bg-white/5 transition-colors"
              >
                Use password instead
                <ArrowRight size={12} />
              </Link>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
